import { lazy, Suspense, useMemo, useState } from 'react';
import {
  Bookmark,
  Clock,
  Globe,
  Plus,
  ScanBarcode,
  Search,
  SearchX,
  Star,
  Trash2,
} from 'lucide-react';
import { Page, EmptyState } from '@/components/Page';
import { inputClass } from '@/components/ui/Field';
import { QuantitySheet } from '@/features/diary/QuantitySheet';
import { defaultSlot, useRecentFoodIds } from '@/features/diary/useDiary';
import { addSavedMeal, deleteSavedMeal, useSavedMeals } from '@/features/diary/savedMeals';
import { FreeTextMeal } from '@/features/llm/FreeTextMeal';
import { isLlmOn } from '@/features/llm/isLlmOn';
import { primeAudio } from '@/features/scanner/feedback';
import { useProfileState } from '@/features/profile/useProfile';
import type { FoodRow } from '@/lib/db';
import { num, todayIso } from '@/lib/format';
import { barcodeForLookup } from '@/features/scanner/detector';
import { lookupBarcode } from '@/lib/off/lookup';
import { keepOffFood, searchOff } from '@/lib/off/search';
import { searchFoods } from '@/lib/search';
import { useCatalog } from '@/lib/useCatalog';

// El escáner arrastra el decodificador de WebAssembly y el formulario de alta
// es una pantalla entera: ninguno de los dos debe pesar en el arranque de la
// app, que es lo que se abre veinte veces al día.
const Scanner = lazy(() =>
  import('@/features/scanner/Scanner').then((m) => ({ default: m.Scanner })),
);
const CustomFoodForm = lazy(() =>
  import('@/features/foods/CustomFoodForm').then((m) => ({ default: m.CustomFoodForm })),
);
const RecipeForm = lazy(() =>
  import('@/features/foods/RecipeForm').then((m) => ({ default: m.RecipeForm })),
);

type Flow =
  | { kind: 'ninguno' }
  | { kind: 'escaneando' }
  | { kind: 'buscando_codigo'; barcode: string }
  | { kind: 'cantidad'; food: FoodRow }
  | { kind: 'elegir_tipo' }
  | { kind: 'crear'; barcode?: string }
  | { kind: 'receta' }
  | { kind: 'aviso'; title: string; hint: string; barcode?: string };

export default function Add() {
  const { status, index, byId, error } = useCatalog();
  const [query, setQuery] = useState('');
  const [flow, setFlow] = useState<Flow>({ kind: 'ninguno' });
  const recentIds = useRecentFoodIds();
  const savedMeals = useSavedMeals();
  const [addingMeal, setAddingMeal] = useState<string | null>(null);
  const [addedMeal, setAddedMeal] = useState<string | null>(null);
  const [offState, setOffState] = useState<
    | { kind: 'listo' }
    | { kind: 'buscando' }
    | { kind: 'hecho'; foods: FoodRow[] }
    | { kind: 'error'; message: string }
  >({ kind: 'listo' });

  // Búsqueda en Open Food Facts: SIEMPRE a mano y después de la local (SPEC
  // 6.B). La cuota de OFF es compartida por todos los usuarios de la app, así
  // que no se dispara sola con cada tecla.
  async function searchInOff() {
    setOffState({ kind: 'buscando' });
    const result = await searchOff(query.trim());
    if (result.status === 'ok') setOffState({ kind: 'hecho', foods: result.foods });
    else if (result.status === 'sin_servidor') {
      setOffState({ kind: 'error', message: 'Hace falta configurar Supabase (ver SETUP.md).' });
    } else setOffState({ kind: 'error', message: result.message });
  }
  const profile = useProfileState();
  const llmOn = profile.status === 'listo' && isLlmOn(profile.profile);

  // Favoritos primero y recientes después: son los dos atajos del spec para lo
  // que se come a diario, y ambos pesan también en el orden de la búsqueda.
  const favorites = useMemo(
    () =>
      [...byId.values()]
        .filter((f) => f.favorite)
        .sort((a, b) => a.name.localeCompare(b.name, 'es')),
    [byId],
  );

  const preferred = useMemo(
    () => new Set([...recentIds, ...favorites.map((f) => f.id)]),
    [recentIds, favorites],
  );

  const results = useMemo(() => {
    if (!index) return [];
    if (query.trim().length < 2) {
      const recents = recentIds
        .map((id) => byId.get(id))
        .filter((f): f is FoodRow => f != null && !f.favorite);
      return [...favorites, ...recents];
    }
    return searchFoods(index, query, { preferred })
      .map((id) => byId.get(id))
      .filter((f): f is FoodRow => f != null);
  }, [index, query, recentIds, byId, preferred, favorites]);

  const showingRecents = query.trim().length < 2;

  async function onBarcode(scanned: string) {
    // Un UPC-E se busca (y se guarda) por su UPC-A, que es como lo indexa OFF.
    const barcode = barcodeForLookup(scanned);
    setFlow({ kind: 'buscando_codigo', barcode });
    const result = await lookupBarcode(barcode);

    switch (result.status) {
      case 'encontrado':
        setFlow({ kind: 'cantidad', food: result.food });
        return;
      case 'no_encontrado':
        setFlow({
          kind: 'aviso',
          title: 'Ese producto no está en Open Food Facts',
          hint: 'Puedes crearlo tú con los datos de la etiqueta. Se guardará para la próxima vez.',
          barcode,
        });
        return;
      case 'sin_datos':
        setFlow({
          kind: 'aviso',
          title: 'El producto existe pero no tiene datos nutricionales',
          hint: 'Créalo con los valores de la etiqueta.',
          barcode,
        });
        return;
      case 'sin_servidor':
        setFlow({
          kind: 'aviso',
          title: 'La búsqueda de productos no está disponible',
          hint: 'Hace falta configurar Supabase (ver SETUP.md). Mientras tanto puedes crear el alimento a mano.',
          barcode,
        });
        return;
      default:
        setFlow({
          kind: 'aviso',
          title: 'No se pudo consultar el producto',
          hint: result.message,
          barcode,
        });
    }
  }

  return (
    <Page title="Añadir" subtitle="Busca, escanea o crea un alimento">
      <div className="flex flex-col gap-4">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 left-3 size-5 -translate-y-1/2 text-[var(--color-text-muted)]"
            />
            <input
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOffState({ kind: 'listo' });
              }}
              placeholder="Arroz, pollo, jamón york…"
              aria-label="Buscar alimento"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              className={`${inputClass} pl-10`}
            />
          </div>
          <button
            type="button"
            onClick={() => {
              // Aquí, dentro del gesto: iOS no deja desbloquear el audio de
              // otra forma, y el escáner ya no recibe ningún toque más antes
              // de leer el código.
              primeAudio();
              setFlow({ kind: 'escaneando' });
            }}
            aria-label="Escanear código de barras"
            className="tap shrink-0 rounded-xl border border-[var(--color-border)] px-3 text-[var(--color-text-muted)]"
          >
            <ScanBarcode aria-hidden="true" className="size-5" />
          </button>
          {/* Siempre disponible: obligar a fallar una búsqueda antes de poder
              crear un alimento es una trampa de diseño. */}
          <button
            type="button"
            onClick={() => setFlow({ kind: 'elegir_tipo' })}
            aria-label="Crear alimento o receta"
            className="tap shrink-0 rounded-xl border border-[var(--color-border)] px-3 text-[var(--color-text-muted)]"
          >
            <Plus aria-hidden="true" className="size-5" />
          </button>
        </div>

        {/* Solo con la capa LLM activada por el usuario (SPEC 8). Sin ella,
            esta caja no existe y la app funciona exactamente igual. Mientras
            se busca se OCULTA (no se desmonta): los resultados van justo debajo
            del buscador y la revisión a medias sigue ahí al volver. */}
        {llmOn && index ? (
          <div hidden={query !== ''}>
            <FreeTextMeal date={todayIso()} index={index} byId={byId} onDone={() => setQuery('')} />
          </div>
        ) : null}

        {/* Comidas guardadas (SPEC 6.B): registrar el desayuno de siempre es
            un toque, no quince. Solo cuando no se está buscando algo concreto. */}
        {showingRecents && savedMeals.length > 0 ? (
          <section aria-label="Comidas guardadas">
            <h2 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-[var(--color-text-muted)]">
              <Bookmark aria-hidden="true" className="size-4" />
              Comidas guardadas
            </h2>
            <ul className="flex flex-col gap-1.5">
              {savedMeals.map((meal) => (
                <li key={meal.id} className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={addingMeal === meal.id}
                    onClick={() => {
                      setAddingMeal(meal.id);
                      void addSavedMeal(meal, todayIso(), defaultSlot()).then((n) => {
                        setAddingMeal(null);
                        setAddedMeal(`${meal.name}: ${n} alimentos añadidos`);
                      });
                    }}
                    className="tap min-w-0 flex-1 rounded-xl border border-[var(--color-border)] px-3 text-left text-sm font-medium disabled:opacity-60"
                  >
                    {meal.name}
                    <span className="block text-xs font-normal text-[var(--color-text-muted)]">
                      {meal.items.length} alimentos
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => void deleteSavedMeal(meal.id)}
                    aria-label={`Borrar la comida guardada ${meal.name}`}
                    className="tap shrink-0 rounded-lg px-2 text-[var(--color-text-muted)]"
                  >
                    <Trash2 aria-hidden="true" className="size-4" />
                  </button>
                </li>
              ))}
            </ul>
            {addedMeal ? (
              <p role="status" className="mt-2 text-xs text-[var(--color-brand-strong)]">
                {addedMeal}
              </p>
            ) : null}
          </section>
        ) : null}

        {error ? (
          <EmptyState
            icon={<SearchX aria-hidden="true" className="size-8" />}
            title="No se pudo cargar el catálogo"
            hint={error}
          />
        ) : status.state === 'cargando' ? (
          <CatalogProgress loaded={status.loaded} total={status.total} />
        ) : results.length === 0 ? (
          <EmptyState
            icon={<SearchX aria-hidden="true" className="size-8" />}
            title={showingRecents ? 'Todavía no has registrado nada' : 'Sin resultados'}
            hint={
              showingRecents
                ? 'Busca un alimento por su nombre, escanea un código o crea el tuyo con el botón +.'
                : 'Prueba con otra palabra. La búsqueda tolera tildes y erratas.'
            }
          />
        ) : (
          <section
            aria-label={
              showingRecents
                ? favorites.length > 0
                  ? 'Favoritos y recientes'
                  : 'Recientes'
                : 'Resultados'
            }
          >
            {showingRecents ? (
              <h2 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-[var(--color-text-muted)]">
                <Clock aria-hidden="true" className="size-4" />
                {favorites.length > 0 ? 'Favoritos y recientes' : 'Recientes'}
              </h2>
            ) : null}
            <ul className="flex flex-col divide-y divide-[var(--color-border)]">
              {results.map((food) => (
                <li key={food.id}>
                  <button
                    type="button"
                    onClick={() => setFlow({ kind: 'cantidad', food })}
                    className="tap flex w-full items-center justify-between gap-3 py-2.5 text-left"
                  >
                    <span className="min-w-0 flex-1 text-sm">
                      {food.favorite ? (
                        <Star
                          aria-label="Favorito"
                          className="mr-1 inline size-3.5 fill-[var(--color-brand)] text-[var(--color-brand)] align-[-2px]"
                        />
                      ) : null}
                      {food.name}
                      {food.brand ? (
                        <span className="text-[var(--color-text-muted)]"> · {food.brand}</span>
                      ) : null}
                    </span>
                    <span className="shrink-0 text-xs text-[var(--color-text-muted)] tabular">
                      {food.per100g.kcal == null ? '—' : `${num(food.per100g.kcal)} kcal`}
                      <span>/100 g</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {/* Después de lo local, Open Food Facts (SPEC 6.B). A mano y solo con
          algo escrito: la cuota de OFF la comparten todos los usuarios. */}
      {!showingRecents && !error && status.state === 'listo' ? (
        <section aria-label="Buscar en Open Food Facts" className="mt-4">
          {offState.kind === 'hecho' ? (
            offState.foods.length === 0 ? (
              <p className="text-sm text-[var(--color-text-muted)]">
                Open Food Facts tampoco tiene nada con «{query.trim()}».
              </p>
            ) : (
              <>
                <h2 className="mb-2 text-sm font-semibold text-[var(--color-text-muted)]">
                  En Open Food Facts
                </h2>
                <ul className="flex flex-col divide-y divide-[var(--color-border)]">
                  {offState.foods.map((food) => (
                    <li key={food.id}>
                      <button
                        type="button"
                        onClick={() => {
                          void keepOffFood(food).then((saved) =>
                            setFlow({ kind: 'cantidad', food: saved }),
                          );
                        }}
                        className="tap flex w-full items-center justify-between gap-3 py-2.5 text-left"
                      >
                        <span className="min-w-0 flex-1 text-sm">
                          {food.name}
                          {food.brand ? (
                            <span className="text-[var(--color-text-muted)]"> · {food.brand}</span>
                          ) : null}
                        </span>
                        <span className="shrink-0 text-xs text-[var(--color-text-muted)] tabular">
                          {food.per100g.kcal == null ? '—' : `${num(food.per100g.kcal)} kcal`}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-[11px] text-[var(--color-text-muted)]">
                  Datos de Open Food Facts, bajo licencia ODbL.
                </p>
              </>
            )
          ) : (
            <button
              type="button"
              disabled={offState.kind === 'buscando' || query.trim().length < 3}
              onClick={() => void searchInOff()}
              className="tap flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[var(--color-border)] px-4 text-sm font-medium text-[var(--color-text-muted)] disabled:opacity-60"
            >
              <Globe aria-hidden="true" className="size-4" />
              {offState.kind === 'buscando'
                ? 'Buscando en Open Food Facts…'
                : 'Buscar en Open Food Facts'}
            </button>
          )}
          {offState.kind === 'error' ? (
            <p role="status" className="mt-2 text-xs text-[var(--color-warn)]">
              {offState.message}
            </p>
          ) : null}
        </section>
      ) : null}

      {flow.kind === 'escaneando' ? (
        <Suspense fallback={<LoadingOverlay label="Abriendo el escáner…" />}>
          <Scanner
            onDetected={(code) => void onBarcode(code)}
            onClose={() => setFlow({ kind: 'ninguno' })}
          />
        </Suspense>
      ) : null}

      {flow.kind === 'buscando_codigo' ? (
        <div
          role="status"
          aria-live="polite"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-8 text-center"
        >
          <p className="rounded-xl bg-[var(--color-surface)] px-4 py-3 text-sm">
            Buscando el producto {flow.barcode}…
          </p>
        </div>
      ) : null}

      {flow.kind === 'aviso' ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={flow.title}
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
        >
          <div className="safe-bottom w-full max-w-lg rounded-t-3xl border-t border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <h2 className="text-base font-semibold">{flow.title}</h2>
            <p className="mt-1 text-sm text-[var(--color-text-muted)]">{flow.hint}</p>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setFlow({ kind: 'ninguno' })}
                className="tap flex-1 rounded-xl border border-[var(--color-border)] px-4 text-sm font-medium"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => setFlow({ kind: 'crear', barcode: flow.barcode })}
                className="tap flex-1 rounded-xl bg-[var(--color-brand-button)] px-4 text-sm font-semibold text-[var(--color-on-brand)]"
              >
                Crear alimento
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {flow.kind === 'elegir_tipo' ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="¿Qué quieres crear?"
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
          onClick={(e) => {
            if (e.target === e.currentTarget) setFlow({ kind: 'ninguno' });
          }}
        >
          <div className="safe-bottom w-full max-w-lg rounded-t-3xl border-t border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <h2 className="text-base font-semibold">¿Qué quieres crear?</h2>
            <div className="mt-3 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => setFlow({ kind: 'crear' })}
                className="tap rounded-xl border border-[var(--color-border)] px-4 text-left text-sm font-medium"
              >
                Un alimento
                <span className="block text-xs font-normal text-[var(--color-text-muted)]">
                  Con los valores de la etiqueta. También para suplementos.
                </span>
              </button>
              <button
                type="button"
                onClick={() => setFlow({ kind: 'receta' })}
                className="tap rounded-xl border border-[var(--color-border)] px-4 text-left text-sm font-medium"
              >
                Una receta
                <span className="block text-xs font-normal text-[var(--color-text-muted)]">
                  Se calcula sola a partir de los ingredientes.
                </span>
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {flow.kind === 'receta' ? (
        <Suspense fallback={<LoadingOverlay label="Abriendo el formulario…" />}>
          <RecipeForm
            onCreated={(food) => setFlow({ kind: 'cantidad', food })}
            onClose={() => setFlow({ kind: 'ninguno' })}
          />
        </Suspense>
      ) : null}

      {flow.kind === 'crear' ? (
        <Suspense fallback={<LoadingOverlay label="Abriendo el formulario…" />}>
          <CustomFoodForm
            barcode={flow.barcode}
            onCreated={(food) => setFlow({ kind: 'cantidad', food })}
            onClose={() => setFlow({ kind: 'ninguno' })}
          />
        </Suspense>
      ) : null}

      {flow.kind === 'cantidad' ? (
        <QuantitySheet
          // La hoja guarda estado propio del alimento (la estimación de
          // micros): al cambiar de alimento tiene que empezar de cero.
          key={flow.food.id}
          food={flow.food}
          date={todayIso()}
          onClose={() => setFlow({ kind: 'ninguno' })}
        />
      ) : null}
    </Page>
  );
}

function LoadingOverlay({ label }: { label: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-8 text-center"
    >
      <p className="rounded-xl bg-[var(--color-surface)] px-4 py-3 text-sm">{label}</p>
    </div>
  );
}

function CatalogProgress({ loaded, total }: { loaded: number; total: number }) {
  const pct = total > 0 ? Math.round((loaded / total) * 100) : 0;
  return (
    <div role="status" aria-live="polite" className="flex flex-col gap-2 py-8 text-center">
      <p className="text-sm font-medium">Preparando el catálogo de alimentos…</p>
      <p className="text-xs text-[var(--color-text-muted)]">
        Solo la primera vez. Después funciona sin conexión.
      </p>
      <div
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        className="mx-auto mt-2 h-2 w-48 overflow-hidden rounded-full bg-[var(--color-surface-2)]"
      >
        <div
          className="h-full rounded-full bg-[var(--color-brand)] transition-[width]"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
